import type { AnswerValue, Question, SurveyResponse } from "./contract";
import { radioV, stripPhaseParents } from "./logic";

/**
 * survey/UniQ プリセット & 固定設問 (PRODUCT_DEV-574 UI/UX)。純粋・副作用なし。
 *
 * 「対象顧客名・ヒアリング相手・実施方法」は全調査の固定必須項目。対象顧客名は回答の顧客
 * そのもの(設問ではない)、ヒアリング相手/実施方法は固定 ID の設問として questions[] に種付けする。
 * プリセット(影響調査)は実施方法の直後に「影響有無/影響項目」を追加する。
 *
 * 固定 ID は builder(削除/必須ロック)・一覧カード(バッジ/実施方法)・完了/詳細(アイコン)で共有。
 */

// ── 固定設問 ID ────────────────────────────────────────────────────────────
export const HEARING_QID = "__hearing";
export const METHOD_QID = "__method";
export const IMPACT_QID = "__impact";
export const IMPACTS_QID = "__impacts";

/** 影響有無 (__impact) の選択値。preset 定義・分岐 phase・impactBadge で共有。 */
export const IMPACT_NO = "no";
export const IMPACT_YES = "yes";

/**
 * システム生成(固定+プリセット)由来の設問 ID の集合。
 * ⑤(改修バッチ5)で builder のロックは撤去済みで、現在は「システム由来か」の述語としてのみ残す
 * (画面の特別描画は HEARING_QID/METHOD_QID 等の個別定数を直接参照している)。
 */
export const MANAGED_QIDS: readonly string[] = [
  HEARING_QID,
  METHOD_QID,
  IMPACT_QID,
  IMPACTS_QID,
];
export const isManagedQid = (qid: string): boolean => MANAGED_QIDS.includes(qid);

// ── 実施方法の選択肢 (表示ラベル + web アイコンキー共通) ──────────────────────
export interface MethodOption {
  v: string;
  label: string;
  iconKey: "phone" | "mail" | "meet" | "other";
  withText?: boolean;
}

export const METHOD_OPTIONS: readonly MethodOption[] = [
  { v: "phone", label: "電話", iconKey: "phone" },
  { v: "mail", label: "メール", iconKey: "mail" },
  { v: "meet", label: "面談", iconKey: "meet" },
  { v: "other", label: "その他", iconKey: "other", withText: true },
];

const methodIconKey = new Map(METHOD_OPTIONS.map((o) => [o.v, o.iconKey]));
/** 実施方法の値からアイコンキーを引く (一覧/完了で lucide 写像に使う)。 */
export const methodIconKeyOf = (v: string | undefined): MethodOption["iconKey"] | undefined =>
  v ? methodIconKey.get(v) : undefined;

const methodLabel = new Map(METHOD_OPTIONS.map((o) => [o.v, o.label]));
/** 実施方法の値から表示ラベル(面談 等)を引く (一覧カードの compact 表示用)。 */
export const methodLabelOf = (v: string | undefined): string | undefined =>
  v ? methodLabel.get(v) : undefined;

// ── 固定設問 (ヒアリング相手 / 実施方法) ─────────────────────────────────────
/** 全調査に種付けする固定必須設問。新規作成時に questions の先頭へ。 */
export const buildFixedQuestions = (): Question[] => [
  {
    questionId: HEARING_QID,
    label: "ヒアリング相手",
    required: true,
    phase: "always",
    type: "text",
    placeholder: "例: 購買部 田所 様",
  },
  {
    questionId: METHOD_QID,
    label: "実施方法",
    required: true,
    phase: "always",
    type: "radio",
    options: METHOD_OPTIONS.map((o) => ({
      v: o.v,
      label: o.label,
      ...(o.withText ? { withText: true } : {}),
    })),
  },
];

// ── プリセット (影響調査) ────────────────────────────────────────────────────
export type SurveyPresetId = "impact-survey";

export interface SurveyPreset {
  id: SurveyPresetId;
  label: string;
  /** 実施方法の直後に追加する設問。 */
  buildQuestions: () => Question[];
}

export const SURVEY_PRESETS: Record<SurveyPresetId, SurveyPreset> = {
  "impact-survey": {
    id: "impact-survey",
    label: "影響調査",
    buildQuestions: () => [
      {
        questionId: IMPACT_QID,
        label: "影響有無",
        required: true,
        phase: "always",
        type: "radio",
        branch: true,
        options: [
          { v: IMPACT_NO, label: "影響なし", tone: "ok", hint: "これで完了" },
          { v: IMPACT_YES, label: "影響あり", tone: "warn", hint: "詳細を入力" },
        ],
      },
      {
        questionId: IMPACTS_QID,
        label: "影響項目",
        required: true,
        phase: `${IMPACT_QID}:${IMPACT_YES}`,
        hint: "該当するもの全て",
        type: "checkbox",
        // 中身は本部が編集する前提。contract は label 非空必須のためプレースホルダで種付け。
        options: [
          { v: "i1", label: "影響項目1" },
          { v: "i2", label: "影響項目2" },
          { v: "i3", label: "影響項目3" },
        ],
      },
    ],
  },
};

/** questions に影響有無(__impact)設問が含まれる = 影響調査プリセット由来か。 */
export const hasImpactPreset = (questions: Question[]): boolean =>
  questions.some((q) => q.questionId === IMPACT_QID);

/** 全プリセットが種付けする設問 ID の集合 (プリセット切替時の除去対象)。 */
const presetSeededQids = (): Set<string> =>
  new Set(
    Object.values(SURVEY_PRESETS).flatMap((p) =>
      p.buildQuestions().map((q) => q.questionId),
    ),
  );

/**
 * プリセット適用の純関数 (builder から使用・単体テスト可能)。
 * 既存のプリセット設問を除去 → presetId 指定時は実施方法(__method)の直後へ seed を挿入
 * (__method 不在なら末尾追記)。除去に伴い、除去された設問を親とする分岐条件(cond)だけを
 * phase から剥がし、他の条件は温存する (全条件が消えたら "always" に戻る)。
 */
export const withPreset = (
  questions: Question[],
  presetId: SurveyPresetId | "",
): Question[] => {
  const seeded = presetSeededQids();
  const base = questions
    .filter((q) => !seeded.has(q.questionId))
    .map((q) => {
      const phase = stripPhaseParents(q.phase, seeded);
      return phase === q.phase ? q : ({ ...q, phase } as Question);
    });
  if (!presetId) return base;
  const seed = SURVEY_PRESETS[presetId].buildQuestions();
  const mIdx = base.findIndex((q) => q.questionId === METHOD_QID);
  return mIdx < 0
    ? [...base, ...seed]
    : [...base.slice(0, mIdx + 1), ...seed, ...base.slice(mIdx + 1)];
};

// ── 回答値ヘルパ (一覧カード/完了で使用) ─────────────────────────────────────
/** 回答から設問 ID の生値を取得。 */
export const getAnswer = (
  resp: SurveyResponse,
  qid: string,
): AnswerValue | undefined => resp.answers.find((a) => a.questionId === qid)?.value;

/** ヒアリング相手(テキスト)。 */
export const hearingOf = (resp: SurveyResponse): string => {
  const v = getAnswer(resp, HEARING_QID);
  return typeof v === "string" ? v : "";
};

/** 実施方法の値(phone/mail/meet/other)。 */
export const methodOf = (resp: SurveyResponse): string | undefined =>
  radioV(getAnswer(resp, METHOD_QID));

/**
 * 影響有無バッジ判定。影響調査プリセットの回答のみ "no"/"yes" を返し、
 * それ以外は null (バッジ非表示)。
 */
export const impactBadge = (
  resp: SurveyResponse,
): typeof IMPACT_NO | typeof IMPACT_YES | null => {
  const v = radioV(getAnswer(resp, IMPACT_QID));
  return v === IMPACT_NO || v === IMPACT_YES ? v : null;
};
